import {
  Manifest,
  RecentClimateTimeMode,
  RecentClimateVariable,
  RecentClimateYearMeta
} from '../types';
import { authenticatedFetch, cleanUrl, getApiServer, getTileUrl } from '../config';
import { TierAccessService, TileAccessDenial } from './TierAccessService';

export const RECENT_CLIMATE_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const RECENT_CLIMATE_MONTH_FULL_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export interface RecentClimateMetricSpec {
  key: RecentClimateVariable;
  icon: string;
  shortLabel: string;
  monthTitle: string;
  ytdTitle: string;
  annualTitle: string;
  monthDesc: string;
  ytdDesc: string;
  annualDesc: string;
  legendType: 'temp' | 'tanom' | 'prcp_month' | 'prcp_ytd' | 'prcpanom' | 'snow';
  conusOnly?: boolean;
}

export const RECENT_CLIMATE_METRICS: RecentClimateMetricSpec[] = [
  {
    key: 'tanom',
    icon: '📈',
    shortLabel: 'Temp Departure',
    monthTitle: 'Temperature Departure',
    ytdTitle: 'YTD Temperature Departure',
    annualTitle: 'Annual Temperature Departure',
    monthDesc: 'Departure vs 1991–2020 normal',
    ytdDesc: 'Year-to-date departure vs 1991–2020 normal',
    annualDesc: 'Full-year departure vs 1991–2020 normal',
    legendType: 'tanom'
  },
  {
    key: 'tavg',
    icon: '🌡️',
    shortLabel: 'Mean Temp',
    monthTitle: 'Average Mean Temp',
    ytdTitle: 'YTD Average Mean Temp',
    annualTitle: 'Annual Average Mean Temp',
    monthDesc: 'Observed daily mean temperature',
    ytdDesc: 'Year-to-date observed daily mean',
    annualDesc: 'Full-year observed daily mean',
    legendType: 'temp'
  },
  {
    key: 'tmax',
    icon: '☀️',
    shortLabel: 'Avg High',
    monthTitle: 'Average High Temp',
    ytdTitle: 'YTD Average High Temp',
    annualTitle: 'Annual Average High Temp',
    monthDesc: 'Daily maximums average',
    ytdDesc: 'Year-to-date average daily high',
    annualDesc: 'Full-year average daily high',
    legendType: 'temp'
  },
  {
    key: 'tmin',
    icon: '❄️',
    shortLabel: 'Avg Low',
    monthTitle: 'Average Low Temp',
    ytdTitle: 'YTD Average Low Temp',
    annualTitle: 'Annual Average Low Temp',
    monthDesc: 'Daily minimums average',
    ytdDesc: 'Year-to-date average daily low',
    annualDesc: 'Full-year average daily low',
    legendType: 'temp'
  },
  {
    key: 'prcp',
    icon: '🌧️',
    shortLabel: 'Total Precip',
    monthTitle: 'Total Precipitation',
    ytdTitle: 'Year-to-Date Precipitation',
    annualTitle: 'Annual Total Precipitation',
    monthDesc: 'Monthly liquid precipitation total',
    ytdDesc: 'Year-to-date liquid precipitation total',
    annualDesc: 'Full-year liquid precipitation total',
    legendType: 'prcp_month'
  },
  {
    key: 'prcpanom',
    icon: '📊',
    shortLabel: 'Precip Departure',
    monthTitle: 'Precipitation Departure',
    ytdTitle: 'YTD Precipitation Departure',
    annualTitle: 'Annual Precipitation Departure',
    monthDesc: 'Departure vs 1991–2020 normal',
    ytdDesc: 'YTD departure & % of 1991–2020 normal',
    annualDesc: 'Full-year departure & % of 1991–2020 normal',
    legendType: 'prcpanom'
  },
  {
    key: 'snow',
    icon: '🌨️',
    shortLabel: 'Snowfall',
    monthTitle: 'Monthly Snowfall',
    ytdTitle: 'Year-to-Date Snowfall',
    annualTitle: 'Annual Total Snowfall',
    monthDesc: 'NOHRSC observed snowfall (CONUS)',
    ytdDesc: 'NOHRSC year-to-date snowfall (CONUS)',
    annualDesc: 'NOHRSC calendar-year snowfall (CONUS)',
    legendType: 'snow',
    conusOnly: true
  }
];

export interface RecentClimateLegendConfig {
  gradient: string;
  labels: string[];
  unit: string;
}

export interface RecentClimateProbeResult {
  valid: boolean;
  year: number;
  timeMode: RecentClimateTimeMode;
  month: number;
  lat: number;
  lon: number;
  source: 'conus_4km' | 'global_55km' | 'none';
  tmaxF: number | null;
  tminF: number | null;
  tavgF: number | null;
  tanomF: number | null;
  prcpIn: number | null;
  prcpNormIn: number | null;
  prcpanomIn: number | null;
  prcpPctNorm: number | null;
  snowIn: number | null;
  elevationM: number | null;
  elevationFt: number | null;
}

interface ParsedGwrcTile {
  kind: 1 | 2;
  rows: number;
  cols: number;
  periodMask: number;
  northDeg: number;
  westDeg: number;
  planes: Int16Array;
  planeSize: number;
  bitToPlaneOffset: Map<number, number>;
}

const MISSING_I16 = -32768;

export class RecentClimateService {
  public static lastAccessDenial: TileAccessDenial | null = null;
  private static tileCache = new Map<string, ParsedGwrcTile | null>();
  private static inflight = new Map<string, Promise<ParsedGwrcTile | null>>();

  public static clearCache(): void {
    this.tileCache.clear();
    this.inflight.clear();
    this.lastAccessDenial = null;
  }

  public static getMetricSpec(variable: RecentClimateVariable): RecentClimateMetricSpec {
    return RECENT_CLIMATE_METRICS.find((m) => m.key === variable) || RECENT_CLIMATE_METRICS[0];
  }

  public static getLegendConfig(
    variable: RecentClimateVariable,
    timeMode: RecentClimateTimeMode,
    isImperial: boolean = true
  ): RecentClimateLegendConfig {
    const spec = this.getMetricSpec(variable);
    const lType = (variable === 'prcp' && timeMode === 'ytd') ? 'prcp_ytd' : spec.legendType;

    switch (lType) {
      case 'temp':
        return {
          gradient: 'linear-gradient(to right, rgb(110,30,170), rgb(30,130,230), rgb(70,220,230), rgb(180,220,60), rgb(245,210,50), rgb(245,140,30), rgb(235,45,35), rgb(150,10,100))',
          labels: isImperial
            ? ['<0°F', '20°', '32°', '50°', '68°', '85°', '105°+']
            : ['<-18°C', '-7°', '0°', '10°', '20°', '29°', '41°+'],
          unit: isImperial ? '°F' : '°C'
        };
      case 'tanom':
        return {
          gradient: 'linear-gradient(to right, rgb(20,40,150), rgb(90,170,240), rgb(230,230,230), rgb(245,130,40), rgb(170,15,60))',
          labels: isImperial
            ? ['-15°F', '-8°', '0° Norm', '+8°', '+15°F']
            : ['-8°C', '-4°', '0° Norm', '+4°', '+8°C'],
          unit: isImperial ? '±°F vs Normal' : '±°C vs Normal'
        };
      case 'prcp_ytd':
        return {
          gradient: 'linear-gradient(to right, rgb(215,210,145), rgb(60,180,120), rgb(30,160,220), rgb(30,85,225), rgb(70,40,200), rgb(170,40,190), rgb(230,50,70))',
          labels: isImperial
            ? ['0.5"', '5"', '15"', '30"', '50"', '85"+']
            : ['13mm', '125mm', '380mm', '760mm', '1270mm', '2150mm+'],
          unit: isImperial ? 'Inches' : 'mm'
        };
      case 'prcp_month':
        return {
          gradient: 'linear-gradient(to right, rgb(180,235,190), rgb(60,180,100), rgb(30,190,220), rgb(40,130,230), rgb(35,85,230), rgb(140,50,210), rgb(220,30,130))',
          labels: isImperial
            ? ['0.05"', '0.5"', '1.5"', '3"', '6"', '12"+']
            : ['1mm', '13mm', '38mm', '75mm', '150mm', '300mm+'],
          unit: isImperial ? 'Inches' : 'mm'
        };
      case 'prcpanom':
        return {
          gradient: 'linear-gradient(to right, rgb(84,48,5), rgb(140,81,10), rgb(191,129,45), rgb(223,194,125), rgb(245,245,245), rgb(128,205,193), rgb(53,151,143), rgb(1,102,94), rgb(0,60,48))',
          labels: isImperial
            ? ['-6" Dry', '-2"', '0" Norm', '+2"', '+6"+ Wet']
            : ['-150mm', '-50mm', '0 Norm', '+50mm', '+150mm+'],
          unit: isImperial ? '±Inches vs Normal' : '±mm vs Normal'
        };
      case 'snow':
      default:
        return {
          gradient: 'linear-gradient(to right, rgb(185,205,245), rgb(130,170,235), rgb(60,120,225), rgb(90,65,210), rgb(150,45,200), rgb(210,50,160), rgb(235,110,110), rgb(255,200,90))',
          labels: isImperial
            ? ['0.5"', '2"', '6"', '12"', '24"', '60"+']
            : ['1cm', '5cm', '15cm', '30cm', '60cm', '150cm+'],
          unit: isImperial ? 'Inches (CONUS)' : 'cm (CONUS)'
        };
    }
  }

  public static getAvailableYears(manifest: Manifest | null | undefined): number[] {
    const yrs = manifest?.recent_climate?.years;
    if (Array.isArray(yrs) && yrs.length > 0) {
      return [...yrs].sort((a, b) => b - a);
    }
    const byYear = manifest?.recent_climate?.by_year;
    if (byYear && typeof byYear === 'object') {
      const keys = Object.keys(byYear)
        .map((k) => parseInt(k, 10))
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => b - a);
      if (keys.length > 0) return keys;
    }
    return [new Date().getFullYear()];
  }

  public static getYearMeta(manifest: Manifest | null | undefined, year: number): RecentClimateYearMeta {
    const found = manifest?.recent_climate?.by_year?.[String(year)];
    if (found) return found;
    const now = new Date();
    const curYr = now.getFullYear();
    const maxM = year < curYr ? 12 : now.getMonth() + 1;
    const months: number[] = [];
    for (let m = 1; m <= maxM; m++) months.push(m);
    return {
      year,
      months,
      has_ytd: true,
      is_complete_year: year < curYr,
      as_of_date: null,
      version: 0,
      variables: ['tmax', 'tmin', 'tavg', 'tanom', 'prcp', 'prcpanom', 'snow'],
      conus_probe_blocks: []
    };
  }

  public static resolveSelection(
    manifest: Manifest | null | undefined,
    rawYear?: number,
    rawMode?: RecentClimateTimeMode,
    rawMonth?: number,
    rawVariable?: RecentClimateVariable
  ): {
    year: number;
    yearMeta: RecentClimateYearMeta;
    timeMode: RecentClimateTimeMode;
    month: number;
    periodTag: string;
    variable: RecentClimateVariable;
  } {
    const years = this.getAvailableYears(manifest);
    const year = (rawYear && years.includes(rawYear)) ? rawYear : years[0];
    const yearMeta = this.getYearMeta(manifest, year);
    const availMonths = (yearMeta.months && yearMeta.months.length > 0) ? yearMeta.months : [1];

    let timeMode: RecentClimateTimeMode = rawMode === 'month' ? 'month' : 'ytd';
    if (timeMode === 'ytd' && yearMeta.has_ytd === false) {
      timeMode = 'month';
    }

    let month = rawMonth || availMonths[availMonths.length - 1];
    if (!availMonths.includes(month)) {
      const earlier = availMonths.filter((m) => m <= month);
      month = earlier.length > 0 ? earlier[earlier.length - 1] : availMonths[availMonths.length - 1];
    }

    const variable: RecentClimateVariable = rawVariable || 'tanom';
    const periodTag = timeMode === 'ytd' ? 'ytd' : `m${String(month).padStart(2, '0')}`;

    return { year, yearMeta, timeMode, month, periodTag, variable };
  }

  public static getPmtilesUrl(
    manifest: Manifest,
    year: number,
    periodTag: string,
    variable: RecentClimateVariable
  ): string {
    return getTileUrl(manifest, 'recent_climate', variable, periodTag, String(year), true);
  }

  public static getConusBlockId(lat: number, normLon: number): string {
    const latSouth = Math.floor(lat / 5) * 5;
    const latNorth = latSouth + 5;
    const lonWest = Math.floor(normLon / 5) * 5;
    const latTag = `${latNorth >= 0 ? 'n' : 's'}${String(Math.abs(latNorth)).padStart(2, '0')}`;
    const lonTag = `${lonWest < 0 ? 'w' : 'e'}${String(Math.abs(lonWest)).padStart(3, '0')}`;
    return `${latTag}_${lonTag}`;
  }

  private static buildProbeUrl(manifest: Manifest, year: number, filename: string, version: number): string {
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const subPath = manifest.recent_climate?.tile_path || 'observed/climate_observed';
    const vQuery = version ? `?v=${encodeURIComponent(String(version))}` : '';
    const raw = cleanUrl(`${apiServer}/${tileRoot}/${subPath}/probe/${year}/${filename}${vQuery}`);
    return (!apiServer && raw.startsWith('/')) ? raw.substring(1) : raw;
  }

  private static async decodeGwrcBuffer(buf: ArrayBuffer): Promise<ParsedGwrcTile | null> {
    if (!buf || buf.byteLength < 16) return null;
    let rawBuf = buf;
    const headU8 = new Uint8Array(buf, 0, 2);
    if (headU8[0] === 0x1f && headU8[1] === 0x8b) {
      if (typeof DecompressionStream === 'undefined') return null;
      const ds = new DecompressionStream('gzip');
      const stream = new Blob([buf]).stream().pipeThrough(ds);
      rawBuf = await new Response(stream).arrayBuffer();
    }
    if (rawBuf.byteLength < 16) return null;

    const dv = new DataView(rawBuf);
    if (
      dv.getUint8(0) !== 0x47 || // 'G'
      dv.getUint8(1) !== 0x57 || // 'W'
      dv.getUint8(2) !== 0x52 || // 'R'
      dv.getUint8(3) !== 0x43    // 'C'
    ) {
      return null;
    }

    const version = dv.getUint8(4);
    const kind = dv.getUint8(5) as 1 | 2;
    if (version !== 1 || (kind !== 1 && kind !== 2)) return null;

    const rows = dv.getUint16(6, true);
    const cols = dv.getUint16(8, true);
    const periodMask = dv.getUint16(10, true);
    const northDeg = dv.getInt16(12, true) / 100.0;
    const westDeg = dv.getInt16(14, true) / 100.0;

    const planeSize = rows * cols;
    const planes = new Int16Array(rawBuf, 16);
    const planesPerPeriod = kind === 2 ? 8 : 7;

    const bitToPlaneOffset = new Map<number, number>();
    let pCursor = 1; // Plane 0 is DEM elevation (meters)
    for (let b = 0; b <= 12; b++) {
      if ((periodMask & (1 << b)) !== 0) {
        bitToPlaneOffset.set(b, pCursor);
        pCursor += planesPerPeriod;
      }
    }

    return {
      kind,
      rows,
      cols,
      periodMask,
      northDeg,
      westDeg,
      planes,
      planeSize,
      bitToPlaneOffset
    };
  }

  private static loadProbeTile(manifest: Manifest, year: number, filename: string, version: number): Promise<ParsedGwrcTile | null> {
    const cacheKey = `${year}:${version}:${filename}`;
    if (this.tileCache.has(cacheKey)) {
      return Promise.resolve(this.tileCache.get(cacheKey) || null);
    }
    const existing = this.inflight.get(cacheKey);
    if (existing) return existing;

    const url = this.buildProbeUrl(manifest, year, filename, version);
    const p = authenticatedFetch(url)
      .then(async (res: Response) => {
        if (!res.ok) {
          const denial = TierAccessService.parseTileAccessDenial(res, 'registered');
          if (denial) {
            this.lastAccessDenial = denial;
            return null;
          }
          this.tileCache.set(cacheKey, null);
          return null;
        }
        this.lastAccessDenial = null;
        const buf = await res.arrayBuffer();
        const parsed = await this.decodeGwrcBuffer(buf);
        this.tileCache.set(cacheKey, parsed);
        return parsed;
      })
      .catch(() => {
        this.tileCache.set(cacheKey, null);
        return null;
      })
      .finally(() => {
        this.inflight.delete(cacheKey);
      });

    this.inflight.set(cacheKey, p);
    return p;
  }

  private static sampleFromTile(
    tile: ParsedGwrcTile,
    periodBit: number,
    row: number,
    col: number,
    year: number,
    timeMode: RecentClimateTimeMode,
    month: number,
    lat: number,
    lon: number
  ): RecentClimateProbeResult | null {
    if (row < 0 || row >= tile.rows || col < 0 || col >= tile.cols) return null;
    const pBase = tile.bitToPlaneOffset.get(periodBit);
    if (pBase === undefined) return null;

    const cellIdx = row * tile.cols + col;
    const ps = tile.planeSize;
    const readScaled = (planeOffset: number, scale: number): number | null => {
      const idx = planeOffset * ps + cellIdx;
      if (idx < 0 || idx >= tile.planes.length) return null;
      const v = tile.planes[idx];
      return v === MISSING_I16 ? null : v / scale;
    };

    const elevM = readScaled(0, 1.0);
    const tmaxF = readScaled(pBase + 0, 10.0);
    const tminF = readScaled(pBase + 1, 10.0);
    const tavgF = readScaled(pBase + 2, 10.0);
    const tanomF = readScaled(pBase + 3, 10.0);
    const prcpIn = readScaled(pBase + 4, 100.0);
    const prcpNormIn = readScaled(pBase + 5, 100.0);
    const prcpanomIn = readScaled(pBase + 6, 100.0);
    const snowIn = tile.kind === 2 ? readScaled(pBase + 7, 10.0) : null;

    if (tavgF === null && tmaxF === null && prcpIn === null && snowIn === null) {
      return null;
    }

    let prcpPctNorm: number | null = null;
    if (prcpIn !== null && prcpNormIn !== null && prcpNormIn >= 0.1) {
      prcpPctNorm = Math.round((prcpIn / prcpNormIn) * 100);
    }

    return {
      valid: true,
      year,
      timeMode,
      month,
      lat,
      lon,
      source: tile.kind === 2 ? 'conus_4km' : 'global_55km',
      tmaxF,
      tminF,
      tavgF,
      tanomF,
      prcpIn,
      prcpNormIn,
      prcpanomIn,
      prcpPctNorm,
      snowIn,
      elevationM: elevM,
      elevationFt: elevM !== null ? Math.round(elevM * 3.28084) : null
    };
  }

  /**
   * Synchronous 0ms probe lookup if the needed spatial block is already warm in RAM,
   * returning `undefined` only when an initial network fetch is required.
   */
  public static probePointSync(
    manifest: Manifest,
    rawYear: number,
    rawMode: RecentClimateTimeMode,
    rawMonth: number,
    lat: number,
    lon: number
  ): RecentClimateProbeResult | undefined {
    const { year, yearMeta, timeMode, month, periodTag } = this.resolveSelection(
      manifest,
      rawYear,
      rawMode,
      rawMonth
    );
    const normLon = ((((lon + 180) % 360) + 360) % 360) - 180;
    if (lat > 84.0 || lat < -60.0) {
      return this.emptyResult(year, timeMode, month, lat, normLon);
    }

    const version = yearMeta.version || 0;
    const periodBit = timeMode === 'ytd' ? 12 : (month - 1);

    // 1. Check CONUS 4.6km 5x5 deg block if inside CONUS bounds
    if (lat >= 24.0 && lat < 50.0 && normLon >= -125.0 && normLon < -65.0) {
      const blockId = this.getConusBlockId(lat, normLon);
      const listedBlocks = yearMeta.conus_probe_blocks;
      const shouldCheckBlock = !listedBlocks || listedBlocks.length === 0 || listedBlocks.includes(blockId);
      if (shouldCheckBlock) {
        const cKey = `${year}:${version}:conus_${blockId}.bin`;
        if (!this.tileCache.has(cKey)) {
          return undefined;
        }
        const cTile = this.tileCache.get(cKey);
        if (cTile) {
          const br = Math.min(cTile.rows - 1, Math.max(0, Math.floor((cTile.northDeg - lat) * 24)));
          const bc = Math.min(cTile.cols - 1, Math.max(0, Math.floor((normLon - cTile.westDeg) * 24)));
          const hit = this.sampleFromTile(cTile, periodBit, br, bc, year, timeMode, month, lat, normLon);
          if (hit) return hit;
        }
      }
    }

    // 2. Fallback to Global 0.5 deg land grid (Alaska, Canada, border zones, global land)
    const gFilename = `global_${periodTag}.bin`;
    const gKey = `${year}:${version}:${gFilename}`;
    if (!this.tileCache.has(gKey)) {
      return undefined;
    }
    const gTile = this.tileCache.get(gKey);
    if (gTile) {
      const gr = Math.min(gTile.rows - 1, Math.max(0, Math.floor((gTile.northDeg - lat) * 2)));
      const gc = Math.min(gTile.cols - 1, Math.max(0, Math.floor((normLon - gTile.westDeg) * 2)));
      const hit = this.sampleFromTile(gTile, periodBit, gr, gc, year, timeMode, month, lat, normLon);
      if (hit) return hit;
    }

    return this.emptyResult(year, timeMode, month, lat, normLon);
  }

  public static async probePoint(
    manifest: Manifest,
    rawYear: number,
    rawMode: RecentClimateTimeMode,
    rawMonth: number,
    lat: number,
    lon: number
  ): Promise<RecentClimateProbeResult> {
    const syncHit = this.probePointSync(manifest, rawYear, rawMode, rawMonth, lat, lon);
    if (syncHit !== undefined) return syncHit;

    const { year, yearMeta, timeMode, month, periodTag } = this.resolveSelection(
      manifest,
      rawYear,
      rawMode,
      rawMonth
    );
    const normLon = ((((lon + 180) % 360) + 360) % 360) - 180;
    if (lat > 84.0 || lat < -60.0) {
      return this.emptyResult(year, timeMode, month, lat, normLon);
    }

    const version = yearMeta.version || 0;
    const periodBit = timeMode === 'ytd' ? 12 : (month - 1);

    if (lat >= 24.0 && lat < 50.0 && normLon >= -125.0 && normLon < -65.0) {
      const blockId = this.getConusBlockId(lat, normLon);
      const listedBlocks = yearMeta.conus_probe_blocks;
      const shouldCheckBlock = !listedBlocks || listedBlocks.length === 0 || listedBlocks.includes(blockId);
      if (shouldCheckBlock) {
        const cTile = await this.loadProbeTile(manifest, year, `conus_${blockId}.bin`, version);
        if (cTile) {
          const br = Math.min(cTile.rows - 1, Math.max(0, Math.floor((cTile.northDeg - lat) * 24)));
          const bc = Math.min(cTile.cols - 1, Math.max(0, Math.floor((normLon - cTile.westDeg) * 24)));
          const hit = this.sampleFromTile(cTile, periodBit, br, bc, year, timeMode, month, lat, normLon);
          if (hit) return hit;
        }
      }
    }

    const gTile = await this.loadProbeTile(manifest, year, `global_${periodTag}.bin`, version);
    if (gTile) {
      const gr = Math.min(gTile.rows - 1, Math.max(0, Math.floor((gTile.northDeg - lat) * 2)));
      const gc = Math.min(gTile.cols - 1, Math.max(0, Math.floor((normLon - gTile.westDeg) * 2)));
      const hit = this.sampleFromTile(gTile, periodBit, gr, gc, year, timeMode, month, lat, normLon);
      if (hit) return hit;
    }

    return this.emptyResult(year, timeMode, month, lat, normLon);
  }

  private static emptyResult(
    year: number,
    timeMode: RecentClimateTimeMode,
    month: number,
    lat: number,
    lon: number
  ): RecentClimateProbeResult {
    return {
      valid: false,
      year,
      timeMode,
      month,
      lat,
      lon,
      source: 'none',
      tmaxF: null,
      tminF: null,
      tavgF: null,
      tanomF: null,
      prcpIn: null,
      prcpNormIn: null,
      prcpanomIn: null,
      prcpPctNorm: null,
      snowIn: null,
      elevationM: null,
      elevationFt: null
    };
  }

  public static formatTemp(valF: number | null, isImperial: boolean, includeUnit: boolean = true): string {
    if (valF === null || Number.isNaN(valF)) return '--';
    const v = isImperial ? valF : ((valF - 32) * 5) / 9;
    return `${v.toFixed(1)}°${includeUnit ? (isImperial ? 'F' : 'C') : ''}`;
  }

  public static formatTempAnomaly(anomF: number | null, isImperial: boolean): string {
    if (anomF === null || Number.isNaN(anomF)) return '--';
    const v = isImperial ? anomF : (anomF * 5) / 9;
    const sign = v > 0 ? '+' : '';
    return `${sign}${v.toFixed(1)}°${isImperial ? 'F' : 'C'}`;
  }

  public static formatPrecip(valIn: number | null, isImperial: boolean): string {
    if (valIn === null || Number.isNaN(valIn)) return '--';
    if (isImperial) return `${valIn.toFixed(2)}"`;
    return `${Math.round(valIn * 25.4)} mm`;
  }

  public static formatPrecipAnomaly(anomIn: number | null, isImperial: boolean): string {
    if (anomIn === null || Number.isNaN(anomIn)) return '--';
    const sign = anomIn > 0 ? '+' : '';
    if (isImperial) return `${sign}${anomIn.toFixed(2)}"`;
    const mm = Math.round(anomIn * 25.4);
    return `${mm > 0 ? '+' : ''}${mm} mm`;
  }

  public static formatSnow(snowIn: number | null, isImperial: boolean): string {
    if (snowIn === null || Number.isNaN(snowIn)) return '--';
    if (isImperial) return `${snowIn.toFixed(1)}"`;
    return `${(snowIn * 2.54).toFixed(1)} cm`;
  }
}
